import React, { useState } from "react";
import { Chip, Popover, Box } from "@mui/material";

interface FilterChipProps {
  label: string;
  activeLabel?: string;
  icon?: React.ReactNode;
  children: (close: () => void) => React.ReactNode;
  id?: string;
}

export const FilterChip: React.FC<FilterChipProps> = ({
  label,
  activeLabel,
  icon,
  children,
  id,
}) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchor);
  const isActive = Boolean(activeLabel);

  const buttonId = id ? `${id}-button` : undefined;
  const popoverId = id ? `${id}-popover` : undefined;

  return (
    <>
      <Chip
        id={buttonId}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={isOpen ? popoverId : undefined}
        icon={icon ? (icon as React.ReactElement) : undefined}
        label={
          <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <span style={{ fontSize: "13px", fontWeight: isActive ? 700 : 500 }}>
              {isActive ? activeLabel : label}
            </span>
            <span style={{ fontSize: "10px", opacity: isActive ? 0.8 : 0.5 }} aria-hidden="true">
              ▾
            </span>
          </Box>
        }
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          height: 36,
          borderRadius: "10px",
          border: "1px solid",
          borderColor: isActive ? "primary.main" : "#E2E8F0",
          bgcolor: isActive ? "primary.main" : "#fff",
          color: isActive ? "#fff" : "text.secondary",
          cursor: "pointer",
          fontWeight: isActive ? 700 : 500,
          transition: "all 150ms ease",
          "& .MuiChip-icon": { color: "inherit", fontSize: 16 },
          "&:hover": {
            borderColor: "primary.main",
            bgcolor: isActive ? "primary.dark" : "#F5F3FF",
            color: isActive ? "#fff" : "primary.main",
          },
        }}
      />
      <Popover
        id={popoverId}
        open={isOpen}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 0.5,
            borderRadius: "12px",
            border: "1px solid #E2E8F0",
            boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
            minWidth: 200,
            p: 0.5,
          },
        }}
      >
        {children(() => setAnchor(null))}
      </Popover>
    </>
  );
};

export default FilterChip;
